import { cva } from 'class-variance-authority'
import { memo } from 'react'

type Size = 'xsmall' | 'small' | 'medium' | 'large'

interface ToggleProps {
  active: boolean
  disabled?: boolean
  onClick: (e: React.MouseEvent) => void
  showLabel?: boolean
  size: Size
}

const toggleContainer = cva(
  'group flex cursor-pointer transition-colors duration-150 ease-in-out flex-shrink-0 rounded-full',
  {
    variants: {
      size: {
        large: 'h-7 w-14 p-0.5 rounded-3.5',
        medium: 'h-6 w-12 p-0.5 rounded-3',
        small: 'h-5 w-10 p-[2px] rounded-2.5',
        xsmall: 'h-4 w-8 p-[1px] rounded-2',
      },
      active: {
        true: 'bg-yellow/60 hover:bg-yellow-80',
        false: 'bg-light-20 hover:bg-light-40',
      },
      disabled: {
        true: 'opacity-50 cursor-not-allowed',
      },
    },
    compoundVariants: [
      {
        active: true,
        disabled: true,
        className: 'hover:bg-yellow/60',
      },
      {
        active: false,
        disabled: true,
        className: 'hover:bg-light-20',
      },
    ],
    defaultVariants: {
      size: 'medium',
      active: false,
      disabled: false,
    },
  },
)

const toggleSwitch = cva('rounded-full transition-all duration-150 ease-in-out', {
  variants: {
    size: {
      large: 'h-6 w-6',
      medium: 'h-5 w-5',
      small: 'h-[16px] w-[16px]',
      xsmall: 'h-3.5 w-3.5',
    },
    active: {
      true: 'bg-light',
      false: 'bg-light-40 group-hover:bg-light',
    },
  },
  compoundVariants: [
    { size: 'large', active: true, className: 'translate-x-7' },
    { size: 'medium', active: true, className: 'translate-x-6' },
    { size: 'small', active: true, className: 'translate-x-5' },
    { size: 'xsmall', active: true, className: 'translate-x-4' },
  ],
  defaultVariants: {
    size: 'medium',
    active: false,
  },
})

const toggleLabel = cva('text-light-40 w-6 text-right', {
  variants: {
    size: {
      large: 'text-xs',
      medium: 'text-xs',
      small: 'text-[11px]',
      xsmall: 'text-[11px]',
    },
  },
  defaultVariants: {
    size: 'medium',
  },
})

export const Toggle: React.FC<ToggleProps> = memo(({ active, size, showLabel, disabled, onClick }) => {
  const ToggleComponent = (
    <div className={toggleContainer({ size, active, disabled })} onClick={disabled ? undefined : onClick}>
      <div className={toggleSwitch({ size, active })} />
    </div>
  )

  if (showLabel === true) {
    return (
      <div className="flex h-fit items-center gap-2">
        <span className={toggleLabel({ size })}>{active ? 'ON' : 'OFF'}</span>
        {ToggleComponent}
      </div>
    )
  }

  return ToggleComponent
})
Toggle.displayName = 'Toggle'
